import React, { useState, useRef, useCallback } from 'react'
import {
  Upload, X, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle,
  Download, ArrowRight, ArrowLeft, Loader2, RefreshCw, ChevronDown
} from 'lucide-react'

const API_URL = process.env.REACT_APP_BACKEND_URL

const ENTITY_LABELS = {
  vehicules: 'Véhicules',
  sites: 'Sites',
  chauffeurs: 'Chauffeurs',
  primes: 'Primes',
}

const FIELD_LABELS = {
  immatriculation: 'Immatriculation',
  marque: 'Marque',
  modele: 'Modèle',
  type_vehicule: 'Type véhicule',
  type_assurance: 'Type assurance',
  statut: 'Statut',
  date_entree: 'Date entrée',
  date_sortie: 'Date sortie',
  notes: 'Notes',
  nom: 'Nom',
  prenom: 'Prénom',
  adresse: 'Adresse',
  code_postal: 'Code postal',
  ville: 'Ville',
  pays: 'Pays',
  email: 'Email',
  telephone: 'Téléphone',
  numero_permis: 'N° Permis',
  numero_police: 'N° Police',
  exercice: 'Exercice',
  prime_ht: 'Prime HT',
  prime_ttc: 'Prime TTC',
  taxes: 'Taxes',
  date_effet: 'Date effet',
  date_echeance: 'Date échéance',
  commentaire: 'Commentaire',
}

const STATUS_CONFIG = {
  ok: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'OK' },
  warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Avertissement' },
  error: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Erreur' },
}

const ACTION_LABELS = {
  create: { label: 'Création', color: 'text-emerald-700', bg: 'bg-emerald-50/80' },
  update: { label: 'Mise à jour', color: 'text-blue-700', bg: 'bg-blue-50/80' },
}

export default function ImportModal({ entityType, tenants, onClose, onDone }) {
  const [step, setStep] = useState(1)
  const [tenantId, setTenantId] = useState(tenants?.[0]?.id || '')
  const [file, setFile] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [mapping, setMapping] = useState({})
  const [importing, setImporting] = useState(false)
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')
  const fileRef = useRef(null)

  const label = ENTITY_LABELS[entityType] || entityType

  // ── Drag & Drop ──
  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true)
    else if (e.type === 'dragleave') setDragActive(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const f = e.dataTransfer.files?.[0]
    if (f && (f.name.endsWith('.xlsx') || f.name.endsWith('.csv'))) {
      setFile(f)
      setError('')
    } else {
      setError('Format non supporté. Utilisez .xlsx ou .csv')
    }
  }, [])

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0]
    if (f) { setFile(f); setError('') }
  }

  // ── Template download ──
  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/import/${entityType}/template`)
      if (!res.ok) throw new Error('Erreur téléchargement')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `template_${entityType}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) { setError(e.message) }
  }

  // ── Step 1 → 2: Upload & Preview ──
  const handlePreview = async () => {
    if (!file || !tenantId) return
    setUploading(true)
    setError('')
    try {
      const formData = new FormData()
      formData.append('tenant_id', tenantId)
      formData.append('file', file)
      const res = await fetch(`${API_URL}/api/import/${entityType}/preview`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        throw new Error(d.detail || `Erreur ${res.status}`)
      }
      const data = await res.json()
      if (data.error) { setError(data.error); return }
      setPreview(data)
      setMapping(data.mapping || {})
      setStep(2)
    } catch (e) { setError(e.message) }
    finally { setUploading(false) }
  }

  // ── Re-preview with updated mapping ──
  const handleRemapPreview = async () => {
    if (!file || !tenantId) return
    setUploading(true)
    setError('')
    try {
      const formData = new FormData()
      formData.append('tenant_id', tenantId)
      formData.append('file', file)
      formData.append('custom_mapping', JSON.stringify(mapping))
      const res = await fetch(`${API_URL}/api/import/${entityType}/preview`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        throw new Error(d.detail || `Erreur ${res.status}`)
      }
      const data = await res.json()
      setPreview(data)
    } catch (e) { setError(e.message) }
    finally { setUploading(false) }
  }

  // ── Step 2 → 3: Execute Import ──
  const handleExecute = async () => {
    if (!file || !tenantId) return
    setImporting(true)
    setError('')
    try {
      const formData = new FormData()
      formData.append('tenant_id', tenantId)
      formData.append('file', file)
      formData.append('custom_mapping', JSON.stringify(mapping))
      const res = await fetch(`${API_URL}/api/import/${entityType}/execute`, {
        method: 'POST',
        body: formData,
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        throw new Error(d.detail || `Erreur ${res.status}`)
      }
      const data = await res.json()
      setReport(data)
      setStep(3)
    } catch (e) { setError(e.message) }
    finally { setImporting(false) }
  }

  const updateMapping = (header, field) => {
    setMapping(prev => {
      const next = { ...prev }
      if (field === '') {
        delete next[header]
      } else {
        next[header] = field
      }
      return next
    })
  }

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" data-testid="import-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col" data-testid="import-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
              <FileSpreadsheet size={16} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm" data-testid="import-modal-title">
                Importer {label}
              </h3>
              <p className="text-[10px] text-slate-400">
                {step === 1 && 'Sélectionnez un fichier Excel ou CSV'}
                {step === 2 && 'Vérifiez le mapping et les données'}
                {step === 3 && 'Rapport d\'import'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StepIndicator current={step} />
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-testid="import-close-btn">
              <X size={16} className="text-slate-400" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 text-red-700 text-xs rounded-lg flex items-start gap-2" data-testid="import-error">
              <XCircle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {step === 1 && (
            <Step1Upload
              tenants={tenants}
              tenantId={tenantId}
              setTenantId={setTenantId}
              file={file}
              setFile={setFile}
              dragActive={dragActive}
              handleDrag={handleDrag}
              handleDrop={handleDrop}
              handleFileSelect={handleFileSelect}
              fileRef={fileRef}
              downloadTemplate={downloadTemplate}
              entityType={entityType}
            />
          )}

          {step === 2 && preview && (
            <Step2Preview
              preview={preview}
              mapping={mapping}
              updateMapping={updateMapping}
              handleRemapPreview={handleRemapPreview}
              uploading={uploading}
              entityType={entityType}
            />
          )}

          {step === 3 && report && (
            <Step3Report report={report} entityType={entityType} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 shrink-0">
          <div>
            {step === 2 && (
              <button
                onClick={() => { setStep(1); setPreview(null); setError('') }}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                data-testid="import-back-btn"
              >
                <ArrowLeft size={13} /> Retour
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step !== 3 && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-xs text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                data-testid="import-cancel-btn"
              >
                Annuler
              </button>
            )}
            {step === 1 && (
              <button
                onClick={handlePreview}
                disabled={!file || !tenantId || uploading}
                className="flex items-center gap-1.5 px-4 py-2 text-xs bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="import-preview-btn"
              >
                {uploading ? <><Loader2 size={13} className="animate-spin" /> Analyse...</> : <>Analyser <ArrowRight size={13} /></>}
              </button>
            )}
            {step === 2 && (
              <button
                onClick={handleExecute}
                disabled={importing || (preview?.nb_error === preview?.total_lignes)}
                className="flex items-center gap-1.5 px-4 py-2 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="import-execute-btn"
              >
                {importing ? <><Loader2 size={13} className="animate-spin" /> Import en cours...</> : <>Lancer l'import <ArrowRight size={13} /></>}
              </button>
            )}
            {step === 3 && (
              <button
                onClick={onDone}
                className="flex items-center gap-1.5 px-5 py-2 text-xs bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
                data-testid="import-done-btn"
              >
                <CheckCircle2 size={13} /> Terminé
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Step Indicator ──────────────────────────────
function StepIndicator({ current }) {
  const steps = [
    { n: 1, label: 'Fichier' },
    { n: 2, label: 'Aperçu' },
    { n: 3, label: 'Rapport' },
  ]
  return (
    <div className="flex items-center gap-1" data-testid="import-step-indicator">
      {steps.map(({ n, label }, i) => (
        <React.Fragment key={n}>
          {i > 0 && <div className={`w-4 h-px ${n <= current ? 'bg-slate-800' : 'bg-slate-200'}`} />}
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
            n === current ? 'bg-slate-800 text-white' :
            n < current ? 'bg-emerald-100 text-emerald-700' :
            'bg-slate-100 text-slate-400'
          }`}>
            {n < current ? <CheckCircle2 size={10} /> : null}
            <span>{label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

// ── Step 1: Upload ──────────────────────────────
function Step1Upload({ tenants, tenantId, setTenantId, file, setFile, dragActive, handleDrag, handleDrop, handleFileSelect, fileRef, downloadTemplate, entityType }) {
  return (
    <div className="space-y-5">
      {/* Tenant select */}
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1.5">Client *</label>
        <select
          value={tenantId}
          onChange={e => setTenantId(e.target.value)}
          className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white focus:ring-2 focus:ring-slate-200 focus:border-slate-300 transition-colors"
          data-testid="import-tenant-select"
        >
          <option value="">Sélectionner un client</option>
          {tenants?.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
        </select>
      </div>

      {/* Drop zone */}
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
          dragActive ? 'border-slate-800 bg-slate-50' :
          file ? 'border-emerald-300 bg-emerald-50/30' :
          'border-slate-200 hover:border-slate-300 hover:bg-slate-50/50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        data-testid="import-dropzone"
      >
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.csv"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="import-file-input"
        />
        {file ? (
          <div className="space-y-2">
            <FileSpreadsheet size={28} className="mx-auto text-emerald-600" />
            <p className="text-sm font-medium text-slate-800">{file.name}</p>
            <p className="text-[10px] text-slate-400">
              {(file.size / 1024).toFixed(1)} Ko
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); setFile(null) }}
              className="text-[10px] text-red-500 hover:text-red-700 underline"
              data-testid="import-remove-file"
            >
              Supprimer le fichier
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload size={28} className="mx-auto text-slate-300" />
            <p className="text-sm text-slate-600">
              Glissez-déposez votre fichier ici
            </p>
            <p className="text-[10px] text-slate-400">
              ou cliquez pour parcourir — formats acceptés : .xlsx, .csv
            </p>
          </div>
        )}
      </div>

      {/* Template download */}
      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
        <div>
          <p className="text-xs font-medium text-slate-700">Template {ENTITY_LABELS[entityType]}</p>
          <p className="text-[10px] text-slate-400">Téléchargez le modèle Excel avec colonnes pré-remplies</p>
        </div>
        <button
          onClick={downloadTemplate}
          className="flex items-center gap-1.5 text-xs text-slate-600 px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-white transition-colors"
          data-testid="import-download-template"
        >
          <Download size={12} /> Template
        </button>
      </div>
    </div>
  )
}

// ── Step 2: Preview + Mapping ───────────────────
function Step2Preview({ preview, mapping, updateMapping, handleRemapPreview, uploading, entityType }) {
  const { headers, available_fields, required_fields, total_lignes, nb_create, nb_update, nb_warning, nb_error, preview_rows } = preview

  return (
    <div className="space-y-5">
      {/* Stats summary */}
      <div className="grid grid-cols-4 gap-3" data-testid="import-preview-stats">
        <StatCard label="Lignes" value={total_lignes} color="text-slate-800" bg="bg-slate-50" />
        <StatCard label="Créations" value={nb_create} color="text-emerald-700" bg="bg-emerald-50" />
        <StatCard label="Mises à jour" value={nb_update} color="text-blue-700" bg="bg-blue-50" />
        <StatCard label="Erreurs" value={nb_error} color="text-red-700" bg="bg-red-50" />
      </div>

      {nb_warning > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg text-xs text-amber-700">
          <AlertTriangle size={13} />
          <span>{nb_warning} ligne(s) avec avertissements — elles seront importées malgré tout</span>
        </div>
      )}

      {/* Column mapping */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-slate-700">Mapping des colonnes</p>
          <button
            onClick={handleRemapPreview}
            disabled={uploading}
            className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-700 transition-colors"
            data-testid="import-remap-btn"
          >
            {uploading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            Recalculer
          </button>
        </div>
        <div className="border border-slate-100 rounded-lg overflow-hidden" data-testid="import-mapping-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                <th className="px-3 py-2 text-left">Colonne du fichier</th>
                <th className="px-3 py-2 text-left">Champ cible</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {headers.map(h => {
                const currentField = mapping[h] || ''
                const isRequired = required_fields.includes(currentField)
                return (
                  <tr key={h} className="hover:bg-slate-50/50">
                    <td className="px-3 py-2 font-medium text-slate-700">{h}</td>
                    <td className="px-3 py-2">
                      <div className="relative">
                        <select
                          value={currentField}
                          onChange={e => updateMapping(h, e.target.value)}
                          className={`w-full text-xs border rounded-md px-2 py-1.5 pr-6 appearance-none bg-white ${
                            isRequired ? 'border-emerald-300 bg-emerald-50/30' :
                            currentField ? 'border-slate-200' : 'border-slate-100 text-slate-400'
                          }`}
                          data-testid={`import-mapping-${h}`}
                        >
                          <option value="">— Ignorer —</option>
                          {available_fields.map(f => (
                            <option key={f} value={f}>
                              {FIELD_LABELS[f] || f}{required_fields.includes(f) ? ' *' : ''}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" />
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Data preview */}
      <div>
        <p className="text-xs font-semibold text-slate-700 mb-2">
          Aperçu des données ({Math.min(preview_rows.length, 20)} / {total_lignes} lignes)
        </p>
        <div className="border border-slate-100 rounded-lg overflow-x-auto max-h-[250px]" data-testid="import-preview-table">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0">
              <tr className="bg-slate-50 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                <th className="px-2 py-2 text-center w-10">L.</th>
                <th className="px-2 py-2 text-center w-16">Action</th>
                <th className="px-2 py-2 text-center w-12">Statut</th>
                {Object.values(mapping).filter(Boolean).map(f => (
                  <th key={f} className="px-2 py-2 text-left">{FIELD_LABELS[f] || f}</th>
                ))}
                <th className="px-2 py-2 text-left">Détails</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {preview_rows.slice(0, 20).map((row, i) => {
                const st = STATUS_CONFIG[row.status] || STATUS_CONFIG.ok
                const act = ACTION_LABELS[row.action] || ACTION_LABELS.create
                const StIcon = st.icon
                return (
                  <tr key={i} className="hover:bg-slate-50/50" data-testid={`import-preview-row-${i}`}>
                    <td className="px-2 py-1.5 text-center text-slate-400">{row.ligne}</td>
                    <td className="px-2 py-1.5 text-center">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${act.bg} ${act.color}`}>
                        {act.label}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <StIcon size={13} className={st.color} />
                    </td>
                    {Object.values(mapping).filter(Boolean).map(f => (
                      <td key={f} className="px-2 py-1.5 text-slate-600 max-w-[120px] truncate">
                        {row.data?.[f] || '—'}
                      </td>
                    ))}
                    <td className="px-2 py-1.5">
                      {row.errors?.length > 0 && (
                        <span className="text-red-600 text-[10px]">{row.errors.map(e => e.erreur).join(', ')}</span>
                      )}
                      {row.warnings?.length > 0 && (
                        <span className="text-amber-600 text-[10px]">{row.warnings.map(w => w.erreur).join(', ')}</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── Step 3: Report ──────────────────────────────
function Step3Report({ report, entityType }) {
  const { total_lignes, crees, mis_a_jour, ignores, erreurs, detail_erreurs } = report
  const success = crees + mis_a_jour
  const successRate = total_lignes > 0 ? Math.round((success / total_lignes) * 100) : 0

  return (
    <div className="space-y-5" data-testid="import-report">
      {/* Success banner */}
      <div className={`p-4 rounded-xl text-center ${
        erreurs === 0 ? 'bg-emerald-50 border border-emerald-100' : 'bg-amber-50 border border-amber-100'
      }`}>
        {erreurs === 0 ? (
          <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
        ) : (
          <AlertTriangle size={32} className="mx-auto text-amber-500 mb-2" />
        )}
        <p className="text-sm font-semibold text-slate-800">
          Import terminé — {successRate}% de réussite
        </p>
        <p className="text-xs text-slate-500 mt-1">
          {success} {ENTITY_LABELS[entityType]?.toLowerCase()} traité(e)s sur {total_lignes}
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-4 gap-3" data-testid="import-report-stats">
        <StatCard label="Total lignes" value={total_lignes} color="text-slate-800" bg="bg-slate-50" />
        <StatCard label="Créés" value={crees} color="text-emerald-700" bg="bg-emerald-50" />
        <StatCard label="Mis à jour" value={mis_a_jour} color="text-blue-700" bg="bg-blue-50" />
        <StatCard label="Erreurs" value={erreurs} color="text-red-700" bg="bg-red-50" />
      </div>

      {ignores > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-600">
          <AlertTriangle size={13} />
          <span>{ignores} doublon(s) ignoré(s)</span>
        </div>
      )}

      {/* Error details */}
      {detail_erreurs?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-red-700 mb-2">Détail des erreurs</p>
          <div className="border border-red-100 rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-red-50 text-[10px] font-semibold text-red-400 uppercase tracking-wider">
                  <th className="px-3 py-2 text-center">Ligne</th>
                  <th className="px-3 py-2 text-left">Erreur</th>
                  <th className="px-3 py-2 text-left">Données</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-50">
                {detail_erreurs.slice(0, 20).map((err, i) => (
                  <tr key={i} className="hover:bg-red-50/50" data-testid={`import-error-row-${i}`}>
                    <td className="px-3 py-1.5 text-center text-red-600 font-medium">{err.ligne}</td>
                    <td className="px-3 py-1.5 text-red-700">
                      {err.errors?.map(e => e.erreur).join(', ')}
                    </td>
                    <td className="px-3 py-1.5 text-slate-500 max-w-[200px] truncate">
                      {Object.entries(err.donnees || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Stat Card ───────────────────────────────────
function StatCard({ label, value, color, bg }) {
  return (
    <div className={`${bg} rounded-lg px-3 py-2.5 text-center`}>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-slate-500">{label}</p>
    </div>
  )
}
